import { useCallback } from "react";
import { dashboardActions } from "redux/Ldb/actions/dashboardActions";
import { useAppDispatch, useAppSelector } from "../storeHooks";

const useAddForecastConfig = (): useAddForecastConfigProp => {
    const dispatch = useAppDispatch();

    const { addingForecastConfig, addForecastConfigSuccess, addForecastConfigFailure } =
        useAppSelector((state) => state.dashboard);

    const addForecastConfig = useCallback(
        async (data: Record<string, any> | Array<Record<string, any>>): Promise<any> => {
            return new Promise((resolve, reject) => {
                // Reset any previous state
                dispatch(dashboardActions.resetAddForecastConfig());

                // Start the Redux action
                dispatch(dashboardActions.addForecastConfig(data));

                // Poll for state changes
                const checkState = () => {
                    const currentState = dispatch((dispatch, getState) => {
                        const state = getState();
                        return {
                            isLoading: state.dashboard.addingForecastConfig,
                            success: state.dashboard.addForecastConfigSuccess,
                            error: state.dashboard.addForecastConfigFailure,
                        };
                    });

                    if (currentState.error) {
                        reject(currentState.error);
                    } else if (currentState.success) {
                        resolve(currentState.success);
                    } else if (
                        !currentState.isLoading &&
                        !currentState.success &&
                        !currentState.error
                    ) {
                        reject("Request failed without error or success response");
                    } else {
                        // Still loading, check again
                        setTimeout(checkState, 100);
                    }
                };

                checkState();
            });
        },
        [dispatch]
    );

    const resetAddForecastConfig = useCallback(() => {
        dispatch(dashboardActions.resetAddForecastConfig());
    }, [dispatch]);

    return [
        addForecastConfig,
        {
            isSuccess: Boolean(addForecastConfigSuccess),
            isError: Boolean(addForecastConfigFailure),
            isLoading: addingForecastConfig,
            data: addForecastConfigSuccess,
            error: addForecastConfigFailure,
            resetAddForecastConfig,
        },
    ];
};

export default useAddForecastConfig;

type useAddForecastConfigProp = [
    (data: ForecastConfiguration) => Promise<any>,
    {
        isSuccess: boolean;
        isError: boolean;
        isLoading: boolean;
        data: any;
        error: any;
        resetAddForecastConfig: () => void;
    }
];

export type ForecastConfiguration = {
    configs: {
        year: string;
        channel: "Retail" | "Shopify" | "Amazon";
        weeklyDemand: {
            week: number;
            quantity: number;
        }[];
        monthlyDemand: {
            month: number;
            quantity: number;
        };
        warehouseId?: string;
        inventoryId: string;
        forecastMethod?: string;
    }[];
};
